import React, { useState, useEffect } from "react";
import {
  Card, CardContent, Typography, Box, TextField, Button,
  Rating, Avatar, IconButton, CircularProgress, Divider,
  Stack, Alert, Tooltip
} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import StarIcon from '@mui/icons-material/Star';
import SendIcon from '@mui/icons-material/Send';

// FIREBASE IMPORTS
import { getAuth } from "firebase/auth";
import {
  collection, addDoc, query, orderBy, onSnapshot,
  doc, deleteDoc, serverTimestamp, getDoc
} from "firebase/firestore";
import { db } from "../firebase";

const getInitials = (name = "") => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length > 1) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase() || "?";
};

const formatCommentDate = (timestamp) => {
  if (!timestamp) return "Just now";
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const now = new Date();
  const diffSec = Math.floor((now - date) / 1000);
  if (diffSec < 60) return "Just now";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 604800) return `${Math.floor(diffSec / 86400)}d ago`;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const RecipeComments = ({
  recipeId,
  currentUserId,
  isAdmin,
  userRating = 0,
  onRatingChange
}) => {
  const auth = getAuth();
  const [comments, setComments] = useState([]);
  const [userProfiles, setUserProfiles] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [commentText, setCommentText] = useState("");
  const [commentRating, setCommentRating] = useState(userRating || 0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Sync comment rating with current recipe user rating if provided
  useEffect(() => {
    if (userRating && userRating > 0) {
      setCommentRating(userRating);
    }
  }, [userRating]);

  // Real-time listener for comments on this recipe
  useEffect(() => {
    if (!recipeId) return;
    setIsLoading(true);

    const commentsRef = collection(db, "recipes", recipeId, "comments");
    const q = query(commentsRef, orderBy("created_at", "desc"));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetched = snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
      setComments(fetched);
      setIsLoading(false);
    }, (err) => {
      console.error("Error listening to comments:", err);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [recipeId]);

  // Fetch and cache live user profiles for commenters to ensure real-time usernames & avatars
  useEffect(() => {
    if (!comments.length) return;
    const uidsToFetch = comments
      .map(c => c.user_id)
      .filter((uid, idx, arr) => uid && arr.indexOf(uid) === idx && !userProfiles[uid]);

    if (!uidsToFetch.length) return;

    uidsToFetch.forEach(async (uid) => {
      try {
        const uSnap = await getDoc(doc(db, "users", uid));
        if (uSnap.exists()) {
          const uData = uSnap.data();
          let cleanUsername = null;
          if (uData.username && !uData.username.includes("@")) {
            cleanUsername = uData.username;
          } else if (uData.first_name || uData.firstName) {
            cleanUsername = `${uData.first_name || uData.firstName} ${uData.last_name || uData.lastName || ""}`.trim();
          } else if (uData.username) {
            cleanUsername = uData.username.split("@")[0];
          }
          setUserProfiles(prev => ({
            ...prev,
            [uid]: {
              username: cleanUsername,
              photoUrl: uData.photoUrl || uData.photo_url || ""
            }
          }));
        }
      } catch (e) {
        console.warn("Failed fetching profile for comment uid:", uid, e);
      }
    });
  }, [comments, userProfiles]);

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;

    const user = auth.currentUser;
    if (!user) {
      alert("Please log in to leave a comment.");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Fetch latest user details (username and profile photo)
      let authorName = "Chef";
      let userPhoto = user.photoURL || "";

      try {
        const userDocRef = doc(db, "users", user.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (userDocSnap.exists()) {
          const uData = userDocSnap.data();
          if (uData.username && !uData.username.includes("@")) {
            authorName = uData.username;
          } else if (uData.first_name || uData.firstName) {
            authorName = `${uData.first_name || uData.firstName} ${uData.last_name || uData.lastName || ""}`.trim();
          } else if (uData.username) {
            authorName = uData.username.split("@")[0];
          } else if (user.displayName && !user.displayName.includes("@")) {
            authorName = user.displayName;
          } else {
            authorName = user.email ? user.email.split("@")[0] : "Chef";
          }
          userPhoto = uData.photoUrl || uData.photo_url || userPhoto;
        } else {
          authorName = (user.displayName && !user.displayName.includes("@"))
            ? user.displayName
            : (user.email ? user.email.split("@")[0] : "Chef");
        }
      } catch (err) {
        console.warn("Could not fetch user profile for comment:", err);
        authorName = (user.displayName && !user.displayName.includes("@"))
          ? user.displayName
          : (user.email ? user.email.split("@")[0] : "Chef");
      }

      // If user selected a rating with this review, update recipe rating
      const effectiveRating = commentRating > 0 ? commentRating : (userRating > 0 ? userRating : null);
      if (commentRating > 0 && onRatingChange) {
        await onRatingChange(null, commentRating);
      }

      const commentData = {
        recipe_id: recipeId,
        user_id: user.uid,
        username: authorName,
        user_photo: userPhoto,
        comment_text: commentText.trim(),
        rating: effectiveRating,
        created_at: serverTimestamp()
      };

      await addDoc(collection(db, "recipes", recipeId, "comments"), commentData);
      setCommentText("");
    } catch (err) {
      console.error("Failed to post comment:", err);
      setError("Failed to post comment. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm("Are you sure you want to delete this comment?")) return;
    try {
      await deleteDoc(doc(db, "recipes", recipeId, "comments", commentId));
    } catch (err) {
      console.error("Failed to delete comment:", err);
      alert("Failed to delete comment. You might not have permission.");
    }
  };

  return (
    <Card sx={{ maxWidth: 800, margin: { xs: "16px auto", sm: "24px auto" }, mx: { xs: 1.5, sm: 'auto' }, borderRadius: 3, boxShadow: 3, '@media print': { display: 'none' } }}>
      <CardContent sx={{ p: { xs: 2, sm: 3.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <ChatBubbleOutlineIcon sx={{ color: '#D28415', fontSize: 28 }} />
          <Typography variant="h5" sx={{ fontFamily: "'Patrick Hand', cursive", color: '#477491' }}>
            Comments & Reviews ({comments.length})
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Post a Comment Box */}
        {currentUserId ? (
          <Box component="form" onSubmit={handleSubmitComment} sx={{ mb: 4, bgcolor: '#faf7f2', p: 2.5, borderRadius: 3, border: '1px solid #eee' }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: '#333' }}>
              Leave a Review or Cooking Note:
            </Typography>

            {/* Rating selector right in the comment box */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1 }}>
              <Typography variant="body2" color="textSecondary">
                Your rating:
              </Typography>
              <Rating
                value={commentRating}
                onChange={(event, newValue) => setCommentRating(newValue || 0)}
                precision={1}
                size="medium"
              />
              {commentRating > 0 && (
                <Typography variant="caption" sx={{ color: '#D28415', fontWeight: 'bold' }}>
                  ({commentRating} of 5 stars)
                </Typography>
              )}
            </Box>

            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="What did you think of this recipe? Did you make any fun family tweaks?"
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              sx={{ bgcolor: 'white', borderRadius: 1 }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting || !commentText.trim()}
                startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                sx={{
                  bgcolor: '#D28415',
                  '&:hover': { bgcolor: '#b36b10' },
                  fontWeight: 'bold',
                  borderRadius: 2
                }}
              >
                {isSubmitting ? "Posting..." : "Post Comment"}
              </Button>
            </Box>
          </Box>
        ) : (
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            Please log in to leave a comment or review on this recipe.
          </Alert>
        )}

        <Divider sx={{ mb: 3 }} />

        {/* List of Comments */}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : comments.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
            <Typography variant="body1">
              No comments yet.
            </Typography>
            <Typography variant="caption">
              Be the first to try this recipe and share your feedback!
            </Typography>
          </Box>
        ) : (
          <Stack spacing={2.5}>
            {comments.map((comment) => {
              const isAuthor = currentUserId === comment.user_id;
              const canDelete = isAuthor || isAdmin;
              const userProf = userProfiles[comment.user_id];
              const displayName = userProf?.username 
                || (comment.username && !comment.username.includes("@") ? comment.username : null)
                || (comment.username ? comment.username.split("@")[0] : "Chef");
              const displayPhoto = userProf?.photoUrl || comment.user_photo || "";

              return (
                <Box
                  key={comment.id}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    border: '1px solid #f0f0f0',
                    transition: 'all 0.2s',
                    '&:hover': { bgcolor: '#f5f5f5' }
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar
                        src={displayPhoto}
                        sx={{
                          bgcolor: '#477491',
                          width: 36,
                          height: 36,
                          fontSize: '0.9rem',
                          fontWeight: 'bold'
                        }}
                      >
                        {getInitials(displayName)}
                      </Avatar>

                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                            {displayName}
                          </Typography>
                          {comment.rating > 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Rating
                                value={comment.rating}
                                readOnly
                                size="small"
                                precision={0.5}
                                icon={<StarIcon fontSize="inherit" sx={{ color: '#ffc107' }} />}
                                emptyIcon={<StarIcon fontSize="inherit" sx={{ color: '#e0e0e0' }} />}
                              />
                            </Box>
                          )}
                        </Box>
                        <Typography variant="caption" color="textSecondary">
                          {formatCommentDate(comment.created_at)}
                        </Typography>
                      </Box>
                    </Box>

                    {canDelete && (
                      <Tooltip title="Delete comment">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteComment(comment.id)}
                          sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>

                  <Typography
                    variant="body1"
                    sx={{
                      ml: { xs: 0, sm: 6 },
                      color: '#333',
                      whiteSpace: 'pre-wrap',
                      fontSize: '0.95rem',
                      lineHeight: 1.5
                    }}
                  >
                    {comment.comment_text}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default RecipeComments;
